import { useContentWidth } from 'src/presentation/hooks/useContentWidth';
import logError from 'src/data/services/logError';
import React, { useState, useEffect, useMemo } from 'react';
import {
	StyleSheet,
	TouchableOpacity,
	View,
	Modal,
	Image,
	StatusBar,
	ScrollView,
	Text,
	Platform,
	useWindowDimensions,
} from 'react-native';
import { afterInteractions } from 'src/core/utils/afterInteractions';
import type { ImageStyle, ViewStyle } from 'react-native';
import showAlert from 'src/core/utils/showAlert';
import errorHandler from 'src/core/utils/errorHandler';
import { Ionicons, MaterialIcons } from '@expo/vector-icons';
import * as Application from 'expo-application';
import Config from 'src/core/utils/Config';
import { Switch } from 'react-native-gesture-handler';
import Loader from 'src/presentation/components/atoms/Loader';
import NavBar from 'src/presentation/components/organisms/NavBar';
import { useAuthenticationStore } from 'src/domain/stores/useAuthenticationStore';
import { useServicesStore } from 'src/domain/stores/useServicesStore';
import { useNotificationStore } from 'src/domain/stores/useNotificationStore';
import { useNavigationStore } from 'src/domain/stores/useNavigationStore';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { SettingsStackParamList } from 'src/presentation/navigation/types/NavigationTypes';
import Colors from 'src/core/utils/Colors';
import Translations from 'src/core/utils/Translations';
import AlertHelper from 'src/presentation/helpers/AlertHelper';
import Fonts from 'src/core/utils/Fonts';
import Dimens from 'src/core/utils/Dimens';
import Constants from 'src/core/utils/Constants';

// Web has no push notifications at all, so the toggle would be a setting that does nothing.
const isPushSupported = Platform.OS !== 'web';

function SettingsScreen() {
	const navigation = useNavigation<NativeStackNavigationProp<SettingsStackParamList>>();
	const authStore = useAuthenticationStore();
	const notificationStore = useNotificationStore();
	const { openUrlInternally } = useServicesStore();
	// The profile image opens in a modal that covers everything, so this one is the window.
	const { height: windowHeight } = useWindowDimensions();
	const contentWidth = useContentWidth();

	const sizedStyles = useMemo(
		(): { modalRoot: ViewStyle; profileImageModal: ImageStyle } => ({
			modalRoot: { width: contentWidth, alignSelf: 'center' },
			// The photo fills the column, not the window: on a wide screen the two differ by nearly
			// a thousand pixels.
			profileImageModal: { width: contentWidth, height: windowHeight },
		}),
		[contentWidth, windowHeight],
	);

	// Opens the privacy policy in the in-app browser (falls back to external, then a copy alert).
	const openPrivacyPolicy = () => openUrlInternally({ url: Translations.urlPrivacyPolicy }).catch(logError);

	const [loading, setLoading] = useState(false);
	const [notifications, setNotifications] = useState(false);
	const [pushToken, setPushToken] = useState('');
	const [modalVisible, setModalVisible] = useState(false);

	useEffect(() => {
		if (!isPushSupported) {
			return;
		}

		notificationStore
			.getPushToken()
			.then((token) => {
				if (token) {
					setNotifications(true);
					setPushToken(token);
				}
			})
			.catch(logError);

		notificationStore
			.getStoredNotificationState()
			.then((state) => {
				// Devices register server-side as active, so default the toggle to on unless the user has explicitly
				// disabled notifications.
				setNotifications(state !== 'disable');
			})
			.catch(logError);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const updateNotificationSettings = (state: boolean) => {
		notificationStore.setPushEnabled(state, pushToken).catch((err) => console.error('error: ', err));
	};

	const setNotificationsValue = () => {
		const newValue = !notifications;
		setNotifications(newValue);
		updateNotificationSettings(newValue);
	};

	const showProfileImage = (visible: boolean) => {
		if (authStore.userProfile?.profile_image) {
			setModalVisible(visible);
		}
	};

	const toProfile = () => {
		if (loading) return;

		// Offline: open the profile with the locally loaded data, no network call or logout.
		if (!useNavigationStore.getState().isConnected) {
			navigation.navigate(Constants.screenProfilePage);
			return;
		}

		setLoading(true);
		authStore
			.getProfile()
			.then(() => {
				setLoading(false);
				// getProfile only logs out on a real 401 (expired session); on network errors you stay logged in.
				if (useAuthenticationStore.getState().isLoggedin) {
					navigation.navigate(Constants.screenProfilePage);
				} else {
					errorHandler(Translations.alertLoggedOutTitle, Translations.alertLoggedOutMessage);
				}
			})
			.catch(() => setLoading(false));
	};

	const logOut = () => {
		AlertHelper.showConfirmAlert({
			title: Translations.alertLogoutTitle,
			message: Translations.alertLogoutMessage,
			confirmText: Translations.alertLogoutTitle,
			confirmButtonStyle: 'destructive',
			onConfirm: () => authStore.logout(),
		});
	};

	const confirmDeleteAccount = () => {
		AlertHelper.showConfirmAlert({
			title: Translations.alertDeleteAccountFinalTitle,
			message: Translations.alertDeleteAccountFinalMessage,
			confirmText: Translations.alertDeleteAccountConfirm,
			confirmButtonStyle: 'destructive',
			onConfirm: () => {
				authStore
					.deleteAccount()
					.then((deleted) => {
						if (!deleted) {
							errorHandler(Translations.errorSomethingWentWrong, Translations.errorTryAgainLater);
							return;
						}
						// Account is gone and the session is cleared: confirm, then land on Home where the
						// logged-out login placeholder shows.
						afterInteractions(() => {
							showAlert({
								title: Translations.alertAccountDeletedTitle,
								message: Translations.alertAccountDeletedMessage,
								onConfirm: () => useNavigationStore.getState().navigate(Constants.screenHome),
							});
						});
					})
					.catch(() => undefined);
			},
		});
	};

	const deleteAccount = () => {
		// The confirm button opens a second, explicit confirmation because the deletion is permanent
		// and irreversible.
		AlertHelper.showConfirmAlert({
			title: Translations.alertDeleteAccountTitle,
			message: Translations.alertDeleteAccountMessage,
			confirmText: Translations.buttonContinue,
			onConfirm: () => afterInteractions(confirmDeleteAccount),
		});
	};

	// Show version + build (e.g.
	// expo-application reads the native bundle, and on web there is none: every field answers null,
	// which rendered the label as "Versie " and nothing else. The version from the Expo config is
	// the same number the build was cut from, so web shows that instead of an empty line.
	const versionLabel = Application.nativeBuildVersion
		? `${Application.nativeApplicationVersion}+${Application.nativeBuildVersion}`
		: (Application.nativeApplicationVersion ?? Config.appVersion);

	return (
		<View style={styles.root}>
			<NavBar
				backBtn={false}
				bgcolor={Colors.primary}
				centerTitle={true}
				color={Colors.black}
				editBtn={false}
				searchBtn={false}
				title={Translations.titleSettings}
			/>
			<ScrollView style={styles.scrollView}>
				{authStore.userProfile && (
					<TouchableOpacity disabled={loading} style={styles.profileCard} onPress={toProfile}>
						<View style={styles.profileTouchable}>
							<View>
								<Text style={styles.profileEmail}>{authStore.userProfile.email}</Text>
								<Text style={styles.profileSubtext}>{Translations.textProfileSubtitle}</Text>
							</View>
							{loading ? <Loader small={true} /> : <MaterialIcons name="arrow-forward" style={styles.profileArrow} />}
						</View>
					</TouchableOpacity>
				)}

				{/* The section holds either the push toggle or the login/register rows: dropping only
					the toggle would leave an empty bordered card behind on web. */}
				{(isPushSupported || !authStore.isLoggedin) && (
					<View style={styles.section}>
						{authStore.isLoggedin ? (
							// Notifications are about your events, and events require an account, so the toggle
							// only makes sense (and only works) when logged in.
							<View style={styles.listItemLast}>
								<View style={styles.notificationLabel}>
									<Text style={styles.listItemText}>{Translations.textReceiveNotifications}</Text>
									<Text style={styles.helperText}>{Translations.textReceiveNotificationsHelper}</Text>
								</View>
								<Switch value={notifications} onValueChange={setNotificationsValue} />
							</View>
						) : (
							<View>
								<TouchableOpacity style={styles.listItem} onPress={() => navigation.navigate(Constants.screenProfileLoginPage)}>
									<Text style={styles.listItemTextPrimary}>{Translations.buttonLogin}</Text>
									<MaterialIcons name="arrow-forward" style={styles.listItemArrow} />
								</TouchableOpacity>
								<TouchableOpacity
									style={styles.listItemLast}
									onPress={() => navigation.navigate(Constants.screenProfileRegistrationPage)}
								>
									<Text style={styles.listItemTextPrimary}>{Translations.buttonRegister}</Text>
									<MaterialIcons name="arrow-forward" style={styles.listItemArrow} />
								</TouchableOpacity>
							</View>
						)}
					</View>
				)}

				<View style={styles.sectionWithMargin}>
					<TouchableOpacity style={styles.listItem} onPress={() => navigation.navigate(Constants.screenFeedbackPage)}>
						<Text style={styles.listItemText}>{Translations.textGiveFeedback}</Text>
						<MaterialIcons name="arrow-forward" style={styles.listItemArrow} />
					</TouchableOpacity>
					<TouchableOpacity style={styles.listItem} onPress={() => navigation.navigate(Constants.screenContactPage)}>
						<Text style={styles.listItemText}>{Translations.titleContact}</Text>
						<MaterialIcons name="arrow-forward" style={styles.listItemArrow} />
					</TouchableOpacity>
					<TouchableOpacity style={styles.listItemLast} onPress={openPrivacyPolicy}>
						<Text style={styles.listItemText}>{Translations.textPrivacyPolicy}</Text>
						<MaterialIcons name="arrow-forward" style={styles.listItemArrow} />
					</TouchableOpacity>
				</View>

				{authStore.userProfile && (
					<View style={styles.sectionSmallMargin}>
						<TouchableOpacity style={styles.logoutItem} onPress={deleteAccount}>
							<View style={styles.deleteRow}>
								<Ionicons name="trash" size={16} style={styles.deleteIcon} />
								<Text style={styles.textRed}>{Translations.textDeleteAccount}</Text>
							</View>
						</TouchableOpacity>
					</View>
				)}

				{authStore.isLoggedin && (
					<View style={styles.sectionWithMargin}>
						<TouchableOpacity style={styles.logoutItem} onPress={logOut}>
							<Text style={styles.logoutText}>{Translations.alertLogoutTitle}</Text>
						</TouchableOpacity>
					</View>
				)}

				<Text style={styles.versionText}>
					{Translations.textVersionNumber} {versionLabel}
				</Text>
			</ScrollView>
			{authStore.userProfile && (
				<Modal
					animationType="slide"
					transparent={false}
					visible={modalVisible}
					onRequestClose={() => setModalVisible(false)}
				>
					{/* Decision D8: modals stay inside the app's column, the profile photo included. */}
					<View style={sizedStyles.modalRoot}>
						<StatusBar backgroundColor="black" barStyle="light-content" />
						<View>
							<Image
								resizeMode="contain"
								source={{ uri: authStore.userProfile.profile_image as string }}
								style={[styles.profileImageModal, sizedStyles.profileImageModal]}
							/>
							<TouchableOpacity style={styles.modalCloseBtn} onPress={() => showProfileImage(!modalVisible)}>
								<MaterialIcons name="close" size={30} style={styles.modalCloseIcon} />
							</TouchableOpacity>
						</View>
					</View>
				</Modal>
			)}
			{authStore.isDeletingAccount && (
				<View style={styles.deleteOverlay}>
					<Loader />
				</View>
			)}
		</View>
	);
}

export default SettingsScreen;

const styles = StyleSheet.create({
	root: {
		flex: 1,
	},
	scrollView: {
		backgroundColor: Colors.greyBg,
	},
	profileCard: {
		backgroundColor: Colors.white,
		marginTop: Dimens.margin32,
		paddingHorizontal: Dimens.padding16,
		paddingVertical: Dimens.padding32,
		flexDirection: 'row',
		borderTopWidth: 1,
		borderBottomWidth: 1,
		borderColor: Colors.greyBorder,
	},
	profileTouchable: {
		flex: 1,
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		paddingLeft: Dimens.padding32,
	},
	profileEmail: {
		fontSize: Dimens.fontSize18,
		fontFamily: Fonts.montserratRegular,
	},
	profileSubtext: {
		color: Colors.greyText,
		fontFamily: Fonts.montserratRegular,
	},
	profileArrow: {
		color: Colors.greySubtle,
		fontSize: Dimens.fontSize22,
	},
	section: {
		backgroundColor: Colors.white,
		borderTopWidth: 1,
		borderBottomWidth: 1,
		borderColor: Colors.greyBorder,
		marginTop: Dimens.margin32,
	},
	sectionWithMargin: {
		backgroundColor: Colors.white,
		marginVertical: Dimens.margin32,
		borderTopWidth: 1,
		borderBottomWidth: 1,
		borderColor: Colors.greyBorder,
	},
	sectionSmallMargin: {
		backgroundColor: Colors.white,
		marginVertical: Dimens.margin8,
		borderTopWidth: 1,
		borderBottomWidth: 1,
		borderColor: Colors.greyBorder,
	},
	listItem: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		borderBottomWidth: 1,
		borderBottomColor: Colors.greyBorder,
		marginLeft: Dimens.margin16,
		paddingLeft: Dimens.padding8,
		paddingRight: Dimens.padding16,
		paddingVertical: Dimens.padding16,
	},
	listItemLast: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		borderBottomWidth: 0,
		marginLeft: Dimens.margin16,
		paddingLeft: Dimens.padding8,
		paddingRight: Dimens.padding16,
		paddingVertical: Dimens.padding16,
	},
	listItemText: {
		fontFamily: Fonts.montserratRegular,
	},
	notificationLabel: {
		flex: 1,
		paddingRight: Dimens.padding16,
	},
	helperText: {
		marginTop: Dimens.margin4,
		fontSize: Dimens.fontSize12,
		color: Colors.greyTextDark,
		fontFamily: Fonts.montserratRegular,
	},
	listItemTextPrimary: {
		color: Colors.secondary,
		fontFamily: Fonts.montserratBold,
	},
	listItemArrow: {
		fontSize: Dimens.fontSize24,
		color: Colors.lightGrey,
	},
	textRed: {
		color: Colors.red,
		fontFamily: Fonts.montserratRegular,
	},
	logoutItem: {
		paddingVertical: Dimens.padding16,
		alignItems: 'center',
		justifyContent: 'center',
	},
	logoutText: {
		color: Colors.red,
		fontFamily: Fonts.montserratRegular,
		textAlign: 'center',
	},
	versionText: {
		textAlign: 'center',
		color: Colors.greyTextDark,
		fontFamily: Fonts.montserratRegular,
		fontSize: Dimens.fontSize14,
		paddingVertical: Dimens.padding16,
	},
	deleteRow: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: 8,
	},
	deleteIcon: {
		color: Colors.red,
	},
	profileImageModal: {
		backgroundColor: Colors.blackPure,
	},
	modalCloseBtn: {
		position: 'absolute',
		backgroundColor: Colors.transparent,
		top: 25,
		right: 5,
		zIndex: 5,
	},
	modalCloseIcon: {
		color: Colors.white,
	},
	deleteOverlay: {
		position: 'absolute',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		backgroundColor: Colors.white,
		opacity: 0.85,
	},
});
